// src/pages/navConfig.jsx
//
// Declarative sidebar nav registry, extracted out of Dashboard.jsx (which
// used to be ~500 lines of hand-written <NavLink>/<SidebarGroup> JSX with
// permission checks scattered inline). Each entry is either a `group`
// (collapsible, can nest — see "rag") or a bare `item`. `visible(ctx)`
// decides whether an entry (and its route) is shown for the current user;
// `ctx` exposes `hasPermission`/`hasAnyPermission` (architecture_scaffold's
// real module:action JWT permissions, resolved via useAuth()),
// `hasModule` (account-scoped module subscription, via useWorkspace()'s
// activeModules), `anyVisible([...])`, and `engagementsCount` — the same
// primitives Dashboard.jsx used to compute its can* flags inline before
// this registry existed. Entries without `visible` are always shown.
//
// Permission strings below are architecture_scaffold's real permissions
// (see seed_permissions_mysql.sql), not the legacy capitalized strings
// ("Users", "CODEBASE", "RAG", …) this file used before — those never
// appear in a token issued by the backend the frontend now talks to (see
// vite.config.js's proxy target), so every one of those checks previously
// evaluated false for a real logged-in user.
//
// Every module section below requires BOTH the permission AND the
// currently-selected project's own account being subscribed to that
// module (`hasModule`) — holding the permission alone was the gap: it let
// a section show (and its 404/402 wait until you clicked in) even when
// the account you're currently working under never paid for it. Sections
// with no matching permission/module in today's catalog yet (Testing,
// Maintenance, Accelerators, RAG, AI Dashboard Studio) are left
// unconditionally visible rather than gated on a string that could never
// match — separately, and not fixable from here: most of those modules'
// controllers aren't mounted in architecture_scaffold yet, so their own
// API calls 404 regardless of this visibility flag.
//
// `section` ("lifecycle" | "platform") groups entries into the sidebar's two
// labeled blocks (Delivery Lifecycle / Platform) introduced with the new
// app-shell (frontend/src/layout/Sidebar.jsx). It's presentation-only —
// Layout.jsx groups by it, nothing else keys off it. Entries without a
// `section` (dashboard, faq) render outside both blocks, pinned top/bottom.

import {
  LayoutDashboard, FileText, Wrench, Users, UserCog, ShieldCheck, History,
  Layers, SlidersHorizontal, FileChartPie, ClipboardList, GitBranch,
  FolderGit2, PlusCircle, Eye, FileSpreadsheet, Cloud,
  Server, Database, Table2, BarChart3, Search, ClipboardCheck, PenTool,
  TestTube2, FileCode2, Rocket, Cog, Sparkles, Puzzle, Cable, Building2,
  Boxes,
} from "lucide-react";
import FaqIcon from "./FaqIcon";

// Connections has no real permission key yet (see
// docs/requirements/connectors-registration.md §4.6) — feature-flagged
// behind this constant so the nav entry/route can be built and demoed
// ahead of the real `connections.manage`-style permission landing. Flip to
// a ctx.hasPermission("connections:read") check (or remove entirely) once
// that exists.
const FEATURE_CONNECTORS_ENABLED = true;

export const NAV_SECTIONS = [
  { key: "dashboard", type: "item", to: "/", end: true, icon: LayoutDashboard, label: "Dashboard" },

  // Not nested under Admin — Source Control/Work Item connections are a
  // flat, per-user-owned model (each user only ever sees the connections
  // *they* personally registered — see
  // list_source_control_connections_handler.py's own docstring), and LLM
  // connections' list route has no permission gate at all (every user sees
  // what's visible to them). Neither is an admin-only resource: registering
  // a Source Control/Work Item connection requires connections:write_personal
  // (granted broadly, to every user — it's always your own, personal
  // connection), while an org/account/project-level LLM one requires the
  // separate, admin-tier connections:write.
  {
    key: "connectors", type: "group", icon: Cable, label: "Connections", section: "platform",
    visible: () => FEATURE_CONNECTORS_ENABLED,
    autoOpenPaths: ["/connectors"],
    items: [
      // Not "Manage Connections" — listing (Source Control/Work Item/LLM) is
      // never permission-gated; only registering one requires a permission
      // (connections:write_personal for Source Control/Work Item,
      // connections:write for an org/account/project-level LLM one). A
      // plain viewer can reach this page too.
      { key: "viewConnectors", to: "/connectors", icon: Cable, label: "Connections" },
    ],
  },

  {
    key: "admin", type: "group", icon: UserCog, label: "Admin", section: "platform",
    // Every item below requires an actual admin-grade permission — unlike
    // Projects (see the standalone group further down), which a
    // project-scoped, non-admin user can also legitimately reach. Nesting
    // that inside "Admin" would visually claim admin rights a viewer
    // doesn't have, so it isn't one of this group's items.
    visible: (ctx) => ctx.anyVisible([
      ctx.hasPermission("admin:manage_users"),
      ctx.hasPermission("identity:roles:view"),
      ctx.hasPermission("identity:permissions:view"),
      ctx.hasPermission("subscriptions:manage"),
      ctx.hasPermission("account:manage"),
    ]),
    autoOpenPaths: ["/users", "/roles", "/permissions", "/admin/subscriptions", "/admin/accounts"],
    items: [
      { key: "users", to: "/users", icon: Users, label: "Users", visible: (ctx) => ctx.hasPermission("admin:manage_users") },
      { key: "roles", to: "/roles", icon: UserCog, label: "Roles", visible: (ctx) => ctx.hasPermission("identity:roles:view") },
      { key: "permissions", to: "/permissions", icon: ShieldCheck, label: "Permissions", visible: (ctx) => ctx.hasPermission("identity:permissions:view") },
      // Real, backend-integrated (account:manage permission, Conductor
      // engagements) — the older duplicate top-level "Accounts" nav group
      // (Manage Accounts) was removed in favor of this.
      { key: "accounts", to: "/admin/accounts", icon: Building2, label: "Accounts", visible: (ctx) => ctx.hasPermission("account:manage") },
      // Org/super admins only — subscriptions:manage is what the backend's
      // activate/deactivate routes actually require (see
      // presentation/shared/subscription/subscription_controller.py).
      { key: "subscriptions", to: "/admin/subscriptions", icon: Boxes, label: "Subscriptions", visible: (ctx) => ctx.hasPermission("subscriptions:manage") },
    ],
  },

  // Its own top-level section, not nested under Admin — deliberately not
  // permission-gated alone: mirrors GET /api/conductor/engagements' own
  // design, where a project-scoped-only user (no org-wide
  // conductor:engagement:view/manage) must still be able to reach their
  // own projects list. Putting this inside "Admin" would make a plain
  // project viewer look like they hold admin rights they don't.
  {
    key: "projects", type: "group", icon: FolderGit2, label: "Projects", section: "platform",
    visible: (ctx) => ctx.anyVisible([
      ctx.hasAnyPermission(["conductor:engagement:view", "conductor:engagement:manage"]),
      ctx.engagementsCount > 0,
    ]),
    autoOpenPaths: ["/admin/projects"],
    items: [
      // Not "Manage Projects" — reachable by a plain project-scoped viewer
      // (see this group's visible() above), who can't actually manage
      // anything here.
      { key: "viewProjects", to: "/admin/projects", icon: FolderGit2, label: "View Projects" },
    ],
  },

  {
    // hasPermissionAnywhere, not hasAnyPermission -- sow:analyze/sow:view are
    // commonly granted account-scoped (sow_analyst/sow_viewer), and that only
    // shows up in scopedPermissions when one of that account's own
    // engagements happens to be the currently-selected project. A real grant
    // must not depend on what's currently selected in the workspace switcher
    // (see the sowRules item below, which needs the same fix but for its own
    // resource).
    key: "planning", type: "group", icon: ClipboardList, label: "Planning", section: "lifecycle",
    visible: (ctx) => ctx.anyVisible([
      ctx.hasPermissionAnywhere(["sow:analyze", "sow:view"]) && ctx.hasModule("sow_analysis"),
      ctx.hasPermissionAnywhere(["sow:rules:view", "sow:rules:manage", "sow:rules:manage_global"]),
      ctx.hasPermissionAnywhere(["sow:reports:view"]) && ctx.hasModule("sow_analysis"),
    ]),
    autoOpenPaths: ["/rules-config", "/sowdraft", "/sowtemplate", "/sow-analysis", "/reports/sow-"],
    // Ordered to match the actual per-deal workflow -- Template first
    // (SowDraftPage.jsx pulls its own template list/selection straight from
    // it: h.templates/selectedTemplateId), then Draft, then Analysis. SOW
    // Rules sits last: it's catalog/admin configuration behind its own
    // sow:rules:* permission, not a step an analyst walks through per SOW,
    // and it isn't tied to whichever account is currently selected either
    // (see its own visible() below).
    items: [
      {
        key: "sowTemplate", to: "/sowtemplate", icon: FileText, label: "SOW Template",
        visible: (ctx) => ctx.hasPermissionAnywhere(["sow:analyze", "sow:view"]) && ctx.hasModule("sow_analysis"),
      },
      {
        key: "sowDraft", to: "/sowdraft", icon: FileText, label: "Draft SOW",
        visible: (ctx) => ctx.hasPermissionAnywhere(["sow:analyze", "sow:view"]) && ctx.hasModule("sow_analysis"),
      },
      {
        // Combines what used to be two nav entries (SOW History Analysis +
        // SOW Analysis Viewer) -- one page now covers both browsing every
        // source (SFDC + manual uploads) and triggering a new analysis.
        key: "sowAnalysis", to: "/sow-analysis", icon: Eye, label: "SOW Analysis",
        visible: (ctx) => ctx.hasPermissionAnywhere(["sow:analyze", "sow:view"]) && ctx.hasModule("sow_analysis"),
      },
      // Rule-catalog management is its own resource (sow:rules:*), separate
      // from running/viewing analyses -- and not tied to whichever account
      // happens to be selected right now, since a sow_org_admin/super_admin
      // manages rules across (or above) accounts, not through one of them.
      // Not gated on hasModule: a global/org-wide rule manager isn't
      // necessarily acting through a subscribed account (see the backend's
      // own choice not to blanket-gate this router by module subscription).
      {
        key: "sowRules", to: "/rules-config", icon: SlidersHorizontal, label: "SOW Rules",
        visible: (ctx) => ctx.hasPermissionAnywhere(["sow:rules:view", "sow:rules:manage", "sow:rules:manage_global"]),
      },
      // Reporting sits last: it describes the three steps above rather
      // than being one of them. hasPermissionAnywhere, not
      // hasAnyPermission, for the same reason as this group's other
      // items -- sow:reports:view is commonly granted account-scoped
      // (sow_analyst/sow_admin), and that only shows up in
      // scopedPermissions when one of that account's engagements happens
      // to be the currently-selected project.
      {
        key: "sowUsageReport", to: "/reports/sow-usage", icon: BarChart3, label: "SOW Usage Report",
        visible: (ctx) => ctx.hasPermissionAnywhere(["sow:reports:view"]) && ctx.hasModule("sow_analysis"),
      },
      {
        key: "sowLogReport", to: "/reports/sow-log", icon: History, label: "SOW Log Report",
        visible: (ctx) => ctx.hasPermissionAnywhere(["sow:reports:view"]) && ctx.hasModule("sow_analysis"),
      },
    ],
  },

  {
    // Collapsed from two entries (Requirement Studio / Design Studio) to
    // one link, pointing at the redesigned studio's single SDLC stage
    // pipeline (redesign plan, "Target shape" — Requirements and Design are
    // now stages inside one project, not separate top-level areas). rds_ui/'s
    // old pages stay reachable by direct URL until Phase 5 deletes them, but
    // are no longer linked from here.
    key: "analysis_design", type: "group", icon: PenTool, label: "Analysis & Design", section: "lifecycle",
    visible: (ctx) => ctx.hasAnyPermission(["rds:generate", "rds:view"]) && ctx.hasModule("rds"),
    autoOpenPaths: ["/studio"],
    items: [
      { key: "studio", to: "/studio", icon: PenTool, label: "Studio" },
    ],
  },

  {
    key: "code", type: "group", icon: GitBranch, label: "Code Quality & Analysis", section: "lifecycle",
    visible: (ctx) => ctx.hasAnyPermission(["codebase:manage", "codebase:scan"]) && ctx.hasModule("codebase"),
    autoOpenPaths: ["/codebase", "/scoringrules"],
    items: [
      // One entry, not two — "Codebase Analysis" and "Repositories" used to be
      // separate items pointing at the same "/codebase" route, and since only
      // "Repositories" had `end: true`, the *other* one stayed lit while
      // viewing a repo's own sub-route ("/codebase/repos/:id"). No `end`
      // here so this item (and only this item) stays active across the list
      // and its detail pages.
      { key: "codebaseRepo", to: "/codebase", icon: FolderGit2, label: "Codebase Analysis" },
      // Add Repo is a modal on the Repositories list itself now (no route to
      // link to). Connections live only at /connectors (see the "Connections"
      // nav group above, and RepoListPage's own "Manage Connections" button) —
      // no duplicate sidebar entry pointing at the same page from here.
      { key: "scoringRules", to: "/scoringrules", icon: SlidersHorizontal, label: "Scoring Rules" },
    ],
  },

  {
    key: "development", type: "group", icon: FileCode2, label: "Development", section: "lifecycle",
    visible: (ctx) => ctx.hasAnyPermission(["adai:read", "adai:generate"]) && ctx.hasModule("adai"),
    items: [
      { key: "adai", to: "/development/adai", icon: FileCode2, label: "Advanced Documentation" },
      { key: "reviewgen", to: "/development/reviewgen", icon: FileCode2, label: "ReviewGen" },
    ],
  },

  {
    // "Testing" (dummy), per the original comment — no testing:* permission
    // exists in architecture_scaffold's catalog yet, so left unconditionally
    // visible rather than gated on a string that could never match.
    key: "testing", type: "group", icon: TestTube2, label: "Testing", section: "lifecycle",
    visible: () => true,
    items: [
      { key: "xenon", to: "/testing/xenon", icon: TestTube2, label: "Xenon AI" },
    ],
  },

  {
    key: "deployment", type: "group", icon: Rocket, label: "Deployment", section: "lifecycle",
    visible: (ctx) => ctx.hasAnyPermission(["golive:view", "golive:approve", "golive:execute"]) && ctx.hasModule("golive"),
    items: [
      { key: "golive", to: "/golive", icon: Rocket, label: "GoLive" },
      { key: "pipelines", to: "/deployment/pipelines", icon: Rocket, label: "Release Pipelines", disabled: true },
      { key: "envHealth", to: "/deployment/health", icon: Rocket, label: "Environment Health", disabled: true },
    ],
  },

  {
    // "Maintenance" (dummy), per the original comment — no maintenance:*
    // permission exists yet either.
    key: "maintenance", type: "group", icon: Cog, label: "Maintenance", section: "lifecycle",
    visible: () => true,
    items: [
      { key: "incidents", to: "/maintenance/incidents", icon: Wrench, label: "Incidents", disabled: true },
      { key: "opsDashboard", to: "/maintenance/ops", icon: Cog, label: "Ops Dashboard", disabled: true },
    ],
  },

  {
    key: "compliance", type: "group", icon: ClipboardCheck, label: "Audit & Compliance", section: "lifecycle",
    visible: (ctx) => ctx.hasAnyPermission(["compliance:view", "compliance:analyze"]) && ctx.hasModule("compliance"),
    autoOpenPaths: ["/compliance"],
    items: [
      { key: "idp", to: "/compliance", icon: ClipboardCheck, label: "Compliance Trackers" },
      { key: "iso", to: "/compliance/iso", icon: ShieldCheck, label: "ISO Assistant", disabled: true },
      { key: "cmmi", to: "/compliance/cmmi", icon: ClipboardCheck, label: "CMMI Assistant", disabled: true },
    ],
  },

  {
    // No accelerators:* permission exists yet — unconditionally visible.
    key: "accelerators", type: "group", icon: Sparkles, label: "Accelerators", section: "platform",
    visible: () => true,
    autoOpenPaths: ["/Accelerators", "/accelerators"],
    items: [
      { key: "acceleratorGallery", to: "/Accelerators", icon: Sparkles, label: "Accelerator Gallery" },
    ],
  },

  {
    // No rag:* permission exists yet — unconditionally visible.
    key: "rag", type: "group", icon: Layers, label: "RAG", section: "platform",
    visible: () => false, // TODO: flip to true once the RAG module is actually implemented
    autoOpenPaths: ["/rag-"],
    items: [
      {
        key: "rag_cloud", type: "group", icon: Cloud, label: "RAG Cloud", defaultOpen: true,
        autoOpenPaths: ["/rag-collections", "/rag-jobs", "/rag-analytics", "/rag-search"],
        items: [
          { key: "ragKb", to: "/rag-collections", icon: Layers, label: "RAG KB" },
          { key: "ragJobs", to: "/rag-jobs", icon: Wrench, label: "RAG Jobs" },
          { key: "ragAnalytics", to: "/rag-analytics", icon: History, label: "RAG Analytics" },
          { key: "ragSearch", to: "/rag-search", icon: Search, label: "RAG Search" },
        ],
      },
      {
        key: "rag_onprem", type: "group", icon: Server, label: "RAG On-Prem",
        autoOpenPaths: ["/rag-onprem"],
        items: [
          { key: "ragOnpremKb", to: "/rag-onprem/kb", icon: Layers, label: "On-Prem KB", disabled: true },
          { key: "ragOnpremJobs", to: "/rag-onprem/jobs", icon: Wrench, label: "On-Prem Jobs", disabled: true },
          { key: "ragOnpremSearch", to: "/rag-onprem/search", icon: Search, label: "On-Prem Search", disabled: true },
        ],
      },
    ],
  },

  {
    // No dashboard:*/studio:* permission exists yet — unconditionally visible.
    key: "studio", type: "group", icon: BarChart3, label: "AI Dashboard Studio", section: "platform",
    visible: () => false, // TODO: flip to true once the Studio module is actually implemented
    autoOpenPaths: ["/datasources", "/datasets", "/csv-datasets", "/dashboards", "/mydashboards"],
    items: [
      { key: "datasource", to: "/datasources", icon: Database, label: "Data Sources" },
      { key: "datasets", to: "/datasets", icon: Table2, label: "Datasets" },
      { key: "csvDatasets", to: "/csv-datasets", icon: FileSpreadsheet, label: "CSV Datasets" },
      { key: "dashboards", to: "/dashboards", icon: LayoutDashboard, label: "Create Dashboard" },
      { key: "mydashboards", to: "/mydashboards", icon: FileChartPie, label: "My Dashboards" },
    ],
  },

  {
    // MCP_AGENT is a real, subscribable module — no mcp:* permission
    // string exists yet, so only the module-subscription half of the
    // gate is enforced today.
    key: "mcp", type: "group", icon: Puzzle, label: "MCP", section: "platform",
    // visible: (ctx) => ctx.hasModule("mcp_agent"),
    visible: () => false,
    items: [
      { key: "mcpHub", to: "/mcp", icon: Server, label: "MCP Hub" },
      { key: "mcpLogs", to: "/mcp/logs", icon: History, label: "MCP Logs", disabled: true },
    ],
  },

  {
    key: "it", type: "group", icon: ClipboardList, label: "IT", section: "platform",
    // visible: (ctx) => ctx.anyVisible([
    //   ctx.hasAnyPermission(["it:manage", "it:view"]),
    //   ctx.hasPermission("IT_REQUEST_CREATE"),
    //   ctx.hasPermission("IT_REQUEST_APPROVE"),
    // ]) && ctx.hasModule("it_service"),
    visible: () => false,
    autoOpenPaths: ["/it"],
    items: [
      {
        key: "itNewRequest", to: "/itnew-request", icon: ClipboardList, label: "Create IT Request",
        visible: (ctx) => ctx.anyVisible([ctx.hasPermission("it:manage"), ctx.hasPermission("IT_REQUEST_CREATE")]),
      },
      {
        key: "myItRequests", to: "/myitrequests", icon: ClipboardList, label: "My IT Requests",
        visible: (ctx) => ctx.anyVisible([ctx.hasPermission("it:manage"), ctx.hasPermission("IT_REQUEST_CREATE")]),
      },
      {
        key: "itChatbot", to: "/itchatbot", icon: ClipboardList, label: "IT Chatbot",
        visible: (ctx) => ctx.anyVisible([ctx.hasPermission("it:manage"), ctx.hasPermission("IT_REQUEST_CREATE")]),
      },
      {
        key: "itApprovals", to: "/itapprovals", icon: ClipboardList, label: "IT Approvals",
        visible: (ctx) => ctx.anyVisible([ctx.hasPermission("it:manage"), ctx.hasPermission("IT_REQUEST_APPROVE")]),
      },
    ],
  },

  { key: "faq", type: "item", to: "/faq", icon: FaqIcon, label: "FAQs" },
];
